import type {
  ChannelHealth,
  DistributionChannelType,
  FeedHealth,
  FeedIngestionState,
  IssueSeverity,
  PublicationState,
  VariantPublication,
} from '../../api';
import type { Level } from '../EnvironmentReadiness';

/**
 * The words the distribution screens use.
 *
 * The API speaks in stable identifiers (`delete_pending`, `not_reported`,
 * `auth_error`); those are contracts, not copy. Everything a person reads
 * goes through here, so the same state always has the same name — and so
 * the difference between "sent" and "live" is stated in words rather than
 * left to a colour. See docs/UX_TERMINOLOGY.md.
 */

export const channelLabel: Record<DistributionChannelType, string> = {
  google_merchant: 'Google Merchant',
  openai_acp: 'OpenAI',
  compari: 'Compari.ro',
  meta_commerce: 'Meta Commerce',
};

export const channelDescription: Record<DistributionChannelType, string> = {
  google_merchant:
    'Products are sent one at a time to a Merchant Center data source. Google reports back whether it accepted each one.',
  openai_acp:
    'The whole catalogue is uploaded as one snapshot over SFTP, under a stable file name, and refreshed after every change.',
  compari:
    'Commerce Ops generates an XML feed and serves it at a private URL. Compari fetches it on its own schedule.',
  meta_commerce:
    'Commerce Ops generates an RSS catalogue and serves it at a private URL. Meta fetches it on the schedule you set in Commerce Manager, replacing the whole catalogue each time, and reports back what it accepted.',
};

export const publicationStateLabel: Record<PublicationState, string> = {
  ineligible: 'Not distributed',
  ready: 'Ready to send',
  queued: 'Waiting to send',
  publishing: 'Sending',
  submitted: 'Sent, awaiting the channel',
  published: 'Live',
  warning: 'Live with warnings',
  rejected: 'Rejected by the channel',
  stale: 'Live, but out of date',
  delete_pending: 'Being removed',
  deleted: 'Removed',
  failed: 'Could not be sent',
};

/**
 * What a state means, only where its label does not say it. "Sent" is
 * deliberately not "live": a successful API call is not an acceptance.
 * A state whose label already says everything has no entry (CO-184).
 */
export const publicationStateHelp: Partial<Record<PublicationState, string>> = {
  ineligible: 'The channel policy or a blocking issue keeps this product off this channel.',
  submitted: 'The channel accepted the request but has not said yet whether the product is live.',
  failed: 'The retries ran out. Retry once the cause is fixed.',
};

export const publicationStateLevel: Record<PublicationState, Level> = {
  ineligible: 'unknown',
  ready: 'unknown',
  queued: 'unknown',
  publishing: 'unknown',
  submitted: 'warning',
  published: 'ok',
  warning: 'warning',
  rejected: 'critical',
  stale: 'warning',
  delete_pending: 'warning',
  deleted: 'unknown',
  failed: 'critical',
};

export const channelHealthLabel: Record<ChannelHealth, string> = {
  connected: 'Connected',
  degraded: 'Not answering',
  auth_error: 'Credentials rejected',
  disabled: 'Turned off',
  unconfigured: 'Not set up yet',
  catalog_error: 'Catalogue not reachable',
  unknown: 'Not checked yet',
};

export const channelHealthLevel: Record<ChannelHealth, Level> = {
  connected: 'ok',
  degraded: 'warning',
  auth_error: 'critical',
  disabled: 'unknown',
  unconfigured: 'warning',
  catalog_error: 'critical',
  unknown: 'unknown',
};

export const feedHealthLabel: Record<FeedHealth, string> = {
  healthy: 'Healthy',
  degraded: 'Serving the previous feed',
  unavailable: 'Not generated yet',
  stale: 'Not refreshed recently',
};

export const feedHealthLevel: Record<FeedHealth, Level> = {
  healthy: 'ok',
  degraded: 'warning',
  unavailable: 'warning',
  stale: 'warning',
};

/**
 * What the channel did with the feed we served it.
 *
 * This is deliberately separate from feed health. Feed health answers "did
 * we produce it"; this answers "did they take it". A healthy feed whose
 * products are all being refused is a real and easily-missed state, and
 * showing one number for both would hide it.
 */
export const feedIngestionLabel: Record<FeedIngestionState, string> = {
  not_reported: 'This channel does not report back',
  pending: 'Not collected yet',
  accepted: 'Accepted',
  partial: 'Accepted with rejections',
  rejected: 'Refused',
};

/** Only the states whose label leaves something out: the rest are fully said by the label. */
export const feedIngestionHelp: Partial<Record<FeedIngestionState, string>> = {
  not_reported:
    'The channel pulls the feed on its own and tells us nothing about what it did with it. Product status here reflects what Commerce Ops sent, not what the channel accepted.',
  partial: 'The ones the channel named are marked below; it reports a sample, so others may be affected too.',
};

export const feedIngestionLevel: Record<FeedIngestionState, Level> = {
  not_reported: 'unknown',
  pending: 'unknown',
  accepted: 'ok',
  partial: 'warning',
  rejected: 'critical',
};

export const severityLabel: Record<IssueSeverity, string> = {
  info: 'For information',
  warning: 'Warning',
  blocking: 'Blocks publishing',
};

export const severityLevel: Record<IssueSeverity, Level> = {
  info: 'unknown',
  warning: 'warning',
  blocking: 'critical',
};

export const variantPublicationLabel: Record<VariantPublication, string> = {
  variants: 'Each variant, grouped by its parent',
  parents: 'The configurable product only',
  both: 'Both the configurable product and each variant',
};

export const runKindLabel: Record<string, string> = {
  initial_sync: 'First catalogue import',
  incremental_sync: 'Catalogue changes',
  reconciliation: 'Reconciliation',
  revalidation: 'Re-check against the new settings',
};

export const runStateLabel: Record<string, string> = {
  running: 'Running',
  completed: 'Finished',
  partial: 'Finished with failures',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** driftLabel names what a reconciliation found, in plain terms. */
export const driftLabel: Record<string, string> = {
  missing_remote: 'The channel had no record of the product',
  stale_remote: 'The channel held an older version',
  price_drift: 'The published price no longer matched',
  availability_drift: 'The published availability no longer matched',
  deleted_still_live: 'A withdrawn product was still live',
  previous_failure: 'An earlier attempt had failed',
  out_of_date_version: 'A newer version was never sent',
  remote_rejected: 'The channel had rejected the product',
  feed_never_built: 'The feed had never been generated',
  failed_ingestion: 'The channel refused part of the feed',
  feed_not_fetched: 'The channel has not collected the feed',
  catalog_unavailable: 'The channel could not be asked about the catalogue',
  remote_unavailable: 'The channel could not be reached',
  source_warning: 'The catalogue read reported a problem',
  invalid_source_product: 'A product could not be read',
};

/** canonicalFieldLabel names the canonical fields an attribute maps into. */
export const canonicalFieldLabel: Record<string, string> = {
  'identity.brand': 'Brand',
  'identity.gtin': 'GTIN / EAN',
  'identity.mpn': 'Manufacturer product number',
  'identity.condition': 'Condition',
  'content.title': 'Title',
  'content.short_description': 'Short description',
  'content.description': 'Description',
  'content.attributes': 'Specifications',
  compatibility: 'Compatibility',
  'commerce.shipping.weight': 'Weight',
  'commerce.shipping.delivery_days_max': 'Delivery time',
};

/** formatMoney renders a canonical amount for display. */
export function formatMoney(amount: { amount_micros: number; currency: string } | undefined): string {
  if (!amount || !amount.currency) return '—';
  const value = amount.amount_micros / 1_000_000;
  return `${value.toFixed(2)} ${amount.currency}`;
}

/** formatBytes renders a feed size. */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** formatWhen renders a timestamp as a short, readable local time. */
export function formatWhen(value?: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
}

/** relativeWhen renders "3m ago" for the freshness lines; the exact time is in a tooltip where the caller adds one. */
export function relativeWhen(value?: string): string {
  if (!value) return 'never';
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - parsed) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
